import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

const GoogleCitySearch = ({
  onAddressChange,
  inputStyle = {},
  placeholder = "Enter city",
  apiKey = process.env.REACT_APP_GOOGLE_API_KEY,
  countryRestrictions = [],
  error,
}) => {
  const inputRef = useRef(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const autocompleteRef = useRef(null);

  // Load Google Maps script
  useEffect(() => {
    if (window.google?.maps?.places) {
      setScriptLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.onload = () => setScriptLoaded(true);
    script.onerror = () => console.error("Google Maps script failed to load");

    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        document.head.removeChild(script);
      }
    };
  }, [apiKey]);

  useEffect(() => {
    if (!scriptLoaded || !inputRef.current) return;

    const options = {
      types: ["(regions)"],
      fields: ["address_components", "formatted_address"],
    };

    if (countryRestrictions.length > 0) {
      options.componentRestrictions = { country: countryRestrictions };
    }

    autocompleteRef.current = new window.google.maps.places.Autocomplete(
      inputRef.current,
      options
    );

    const handlePlaceChange = () => {
      const place = autocompleteRef.current.getPlace();
      if (!place?.address_components) return;

      const addressComponents = {};
      place.address_components.forEach((component) => {
        component.types.forEach((type) => {
          addressComponents[type] = component.long_name;
        });
      });

      const city =
        addressComponents.locality ||
        addressComponents.administrative_area_level_2 ||
        "";
      const state = addressComponents.administrative_area_level_1 || "";
      const country = addressComponents.country || "";

      const formattedAddress = [city, state, country]
        .filter(Boolean)
        .join(", ");

      if (inputRef.current) {
        inputRef.current.value = formattedAddress;
      }

      onAddressChange({
        city,
        state,
        country,
        formattedAddress,
      });
    };

    autocompleteRef.current.addListener("place_changed", handlePlaceChange);

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(
          autocompleteRef.current
        );
      }
    };
  }, [scriptLoaded, countryRestrictions, onAddressChange]);

  return (
    <div className={`anvCustom_floatInput form-floating address-autocomplete ${error && "anvInputError"}`}>
      <input
        className="form-control"
        ref={inputRef}
        placeholder={placeholder}
        style={{ ...inputStyle }}
        required
      />
      <label htmlFor="floatingInput">
        <span>{placeholder}</span>
      </label>
      {error && (
        <span className={"input_drop_error"}>{error}</span>
      )}
    </div>
  );
};

GoogleCitySearch.propTypes = {
  onAddressChange: PropTypes.func.isRequired,
  inputStyle: PropTypes.object,
  placeholder: PropTypes.string,
  apiKey: PropTypes.string,
  countryRestrictions: PropTypes.arrayOf(PropTypes.string),
};

GoogleCitySearch.defaultProps = {
  inputStyle: {},
  placeholder: "Enter city",
  countryRestrictions: [],
};

export default GoogleCitySearch;
